import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AdminNavbar from '../../components/AdminNavbar';
import { fetchAdminOrders, updateOrderStatus } from '../../store/slices/adminSlice';

const AdminOrdersPage = () => {
    const dispatch = useDispatch();
    const { authUser, orders, ordersLoading } = useSelector((state) => state.admin);
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedOrder, setSelectedOrder] = useState(null);

    useEffect(() => {
        if (authUser?.uid) {
            dispatch(fetchAdminOrders(authUser.uid));
        }
    }, [dispatch, authUser]);

    const handleStatusChange = (orderID, newStatus) => {
        dispatch(updateOrderStatus({ userID: authUser.uid, orderID, status: newStatus }));
        if (selectedOrder?.id === orderID) {
            setSelectedOrder(prev => ({ ...prev, status: newStatus }));
        }
    };

    const filteredOrders = orders.filter(o => selectedStatus === 'all' || o.status === selectedStatus);

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'pending': return 'bg-warning-100 text-warning-700 border-warning-200';
            case 'confirmed': return 'bg-info-100 text-info-700 border-info-200';
            case 'preparing': return 'bg-primary-100 text-primary-700 border-primary-200';
            case 'ready': return 'bg-success-100 text-success-700 border-success-200';
            case 'delivered': return 'bg-gray-100 text-gray-700 border-gray-200';
            case 'cancelled': return 'bg-danger-100 text-danger-700 border-danger-200';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'pending': return 'Pendiente';
            case 'confirmed': return 'Confirmado';
            case 'preparing': return 'En Preparación';
            case 'ready': return 'Listo para Entregar';
            case 'delivered': return 'Entregado';
            case 'cancelled': return 'Cancelado';
            default: return status || 'Pendiente';
        }
    };

    return (
        <div className="bg-gray-50 min-vh-100 font-sans pb-60">
            <AdminNavbar />

            <div className="container container-lg py-32">
                <div className="d-flex align-items-center justify-content-between mb-24 flex-wrap gap-12">
                    <div>
                        <div className="d-flex align-items-center gap-8 mb-4">
                            <h4 className="fw-bold text-gray-900 mb-0">Gestión de Pedidos Recibidos</h4>
                            <span className="badge bg-warning-100 text-warning-700 rounded-pill text-3xs">DEV ONLY</span>
                        </div>
                        <p className="text-gray-500 text-xs mb-0">
                            Revisa y actualiza el estado de los pedidos realizados por tus clientes desde tus catálogos
                        </p>
                    </div>

                    {/* Filter Tabs */}
                    <div className="d-flex align-items-center gap-6 overflow-x-auto pb-4">
                        {['all', 'pending', 'confirmed', 'preparing', 'ready', 'delivered', 'cancelled'].map(st => (
                            <button
                                key={st}
                                type="button"
                                onClick={() => setSelectedStatus(st)}
                                className={`btn btn-sm rounded-pill px-14 py-6 text-xs fw-semibold whitespace-nowrap transition-2 ${
                                    selectedStatus === st ? 'btn-main' : 'btn-outline-gray-200 text-gray-700 hover-bg-gray-100'
                                }`}
                            >
                                {st === 'all' ? 'Todos' : getStatusLabel(st)}
                            </button>
                        ))}
                    </div>
                </div>

                {ordersLoading ? (
                    <div className="text-center py-60 bg-white rounded-24 border">
                        <div className="spinner-border text-main-600 mb-12" role="status"></div>
                        <p className="text-gray-400 text-xs">Cargando pedidos...</p>
                    </div>
                ) : filteredOrders.length === 0 ? (
                    <div className="text-center py-60 bg-white rounded-24 border">
                        <i className="ph ph-clipboard-text text-4xl text-gray-300 mb-12"></i>
                        <h6 className="fw-bold text-gray-700 mb-4">No hay pedidos {selectedStatus !== 'all' ? 'en este estado' : ''}</h6>
                        <p className="text-gray-400 text-xs mb-0">Los pedidos realizados por clientes aparecerán automáticamente aquí.</p>
                    </div>
                ) : (
                    <div className="row g-24">
                        {/* Orders List Table */}
                        <div className="col-lg-7">
                            <div className="bg-white rounded-24 border shadow-2xs overflow-hidden">
                                <div className="p-16 border-bottom bg-gray-50 flex-between">
                                    <span className="fw-bold text-xs text-gray-700 text-uppercase">Mostrando {filteredOrders.length} pedido(s)</span>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0 text-sm">
                                        <thead className="bg-gray-50 text-gray-500 text-2xs text-uppercase">
                                            <tr>
                                                <th className="py-12 px-16">Fecha & Cliente</th>
                                                <th className="py-12 px-16">Catálogo</th>
                                                <th className="py-12 px-16">Total</th>
                                                <th className="py-12 px-16 text-end">Estado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredOrders.map(order => (
                                                <tr 
                                                    key={order.id} 
                                                    onClick={() => setSelectedOrder(order)}
                                                    className={`cursor-pointer transition-2 ${selectedOrder?.id === order.id ? 'table-active' : ''}`}
                                                >
                                                    <td className="py-12 px-16">
                                                        <div className="fw-bold text-gray-900 text-xs mb-2">{order.clientName || 'Cliente Anónimo'}</div>
                                                        <div className="text-2xs text-gray-400">
                                                            {order.createdAt ? new Date(order.createdAt).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : 'Reciente'}
                                                        </div>
                                                    </td>
                                                    <td className="py-12 px-16 text-xs text-gray-600">
                                                        {order.catalogName || 'Catálogo'}
                                                    </td>
                                                    <td className="py-12 px-16 fw-bold text-gray-900 text-xs">
                                                        {order.currency || '$'}{order.total?.toFixed(2)}
                                                    </td>
                                                    <td className="py-12 px-16 text-end">
                                                        <span className={`badge border rounded-pill px-10 py-4 text-2xs fw-bold ${getStatusBadgeClass(order.status)}`}>
                                                            {getStatusLabel(order.status)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Order Detail Side Drawer */}
                        <div className="col-lg-5">
                            {selectedOrder ? (
                                <div className="bg-white rounded-24 border shadow-2xs p-24 position-sticky" style={{ top: '80px' }}>
                                    <div className="d-flex align-items-center justify-content-between mb-20 pb-16 border-bottom">
                                        <div>
                                            <span className="text-2xs text-gray-400 text-uppercase fw-bold d-block">Detalle del Pedido</span>
                                            <h6 className="fw-bold text-gray-900 mb-0">#{selectedOrder.id.slice(-6).toUpperCase()}</h6>
                                        </div>
                                        <button 
                                            type="button" 
                                            className="btn-close"
                                            onClick={() => setSelectedOrder(null)}
                                        ></button>
                                    </div>

                                    {/* Client Contact Info */}
                                    <div className="p-16 rounded-16 bg-gray-50 border mb-20">
                                        <h6 className="text-2xs text-gray-400 text-uppercase fw-bold mb-12">Información del Cliente</h6>
                                        <div className="d-flex flex-column gap-8 text-xs">
                                            <div className="d-flex align-items-center gap-8">
                                                <i className="ph ph-user text-main-600 text-md"></i>
                                                <span className="fw-bold text-gray-900">{selectedOrder.clientName}</span>
                                            </div>
                                            {selectedOrder.clientPhone && (
                                                <div className="d-flex align-items-center gap-8">
                                                    <i className="ph ph-phone text-success text-md"></i>
                                                    <a href={`tel:${selectedOrder.clientPhone}`} className="text-gray-700 hover-text-main-600 text-decoration-none">
                                                        {selectedOrder.clientPhone}
                                                    </a>
                                                </div>
                                            )}
                                            {selectedOrder.clientEmail && (
                                                <div className="d-flex align-items-center gap-8">
                                                    <i className="ph ph-envelope-simple text-warning text-md"></i>
                                                    <a href={`mailto:${selectedOrder.clientEmail}`} className="text-gray-700 hover-text-main-600 text-decoration-none">
                                                        {selectedOrder.clientEmail}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Change Status Dropdown */}
                                    <div className="mb-20">
                                        <label className="form-label text-2xs fw-bold text-gray-700 text-uppercase">Cambiar Estado del Pedido</label>
                                        <select 
                                            className="form-select rounded-12 text-xs py-8 px-12 fw-semibold"
                                            value={selectedOrder.status || 'pending'}
                                            onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value)}
                                        >
                                            <option value="pending">⏳ Pendiente</option>
                                            <option value="confirmed">✅ Confirmado</option>
                                            <option value="preparing">🍳 En Preparación</option>
                                            <option value="ready">📦 Listo para Entregar</option>
                                            <option value="delivered">🎉 Entregado</option>
                                            <option value="cancelled">❌ Cancelado</option>
                                        </select>
                                    </div>

                                    {/* Items List */}
                                    <div className="mb-20">
                                        <h6 className="text-2xs text-gray-400 text-uppercase fw-bold mb-12">Productos Pedidos</h6>
                                        <div className="d-flex flex-column gap-8">
                                            {selectedOrder.items?.map((item, idx) => (
                                                <div key={idx} className="d-flex align-items-center justify-content-between text-xs py-6 border-bottom border-gray-100">
                                                    <div>
                                                        <span className="fw-semibold text-gray-900">{item.productName}</span>
                                                        <span className="text-gray-400 ms-6">x{item.quantity}</span>
                                                    </div>
                                                    <span className="fw-bold text-gray-900">
                                                        {selectedOrder.currency || '$'}{item.subtotal?.toFixed(2)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Total */}
                                    <div className="pt-16 border-top d-flex align-items-center justify-content-between">
                                        <span className="fw-bold text-gray-900 text-sm">Total del Pedido:</span>
                                        <span className="fw-extrabold text-main-600 text-lg">
                                            {selectedOrder.currency || '$'}{selectedOrder.total?.toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-white rounded-24 border p-32 text-center text-gray-400">
                                    <i className="ph ph-hand-pointing text-3xl mb-8"></i>
                                    <p className="text-xs mb-0">Selecciona un pedido de la lista para ver los detalles y actualizar su estado.</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminOrdersPage;
